import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldAlert } from "@fg/icons";
import { connectParityApi } from "@/api/connectApi";
import { MeetingRoom } from "./MeetingRoom";
import { PreJoin, type PreJoinChoice } from "./PreJoin";
import { loadGuestIdentity, saveGuestIdentity, type GuestIdentity } from "./meeting/guestIdentity";

/**
 * Public guest-join landing page (/guest/:meetingId). Lets someone WITHOUT an
 * Fowlguard account join a meeting straight from a browser — no sign-in.
 *
 * The green room itself is the shared {@link PreJoin} screen, so a guest gets
 * exactly what a signed-in user gets — live preview, mic level, and camera /
 * microphone / speaker pickers — plus the name field guests need.
 *
 * The guest keeps one identity per meeting (see guestIdentity.ts): a reconnect
 * or a refresh is the same guest, so an admitted guest isn't sent back to the
 * lobby and doesn't vanish from everyone's screen.
 */
export function GuestJoinPage() {
    const { meetingId } = useParams<{ meetingId: string }>();
    const [info, setInfo] = useState<{ subject: string; joinable: boolean; kind: "audio" | "video"; scheduledEnd: string | null } | null>(null);
    const [status, setStatus] = useState<"loading" | "ready" | "notfound">("loading");
    const [choice, setChoice] = useState<PreJoinChoice | null>(null);
    const [identity, setIdentity] = useState<GuestIdentity | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (!meetingId) { setStatus("notfound"); return; }
        void connectParityApi.publicMeeting(meetingId)
            .then((m) => {
                if (cancelled) return;
                setInfo({ subject: m.subject, joinable: m.joinable, kind: m.kind ?? "video", scheduledEnd: m.scheduledEnd ?? null });
                setStatus("ready");
            })
            .catch(() => { if (!cancelled) setStatus("notfound"); });
        return () => { cancelled = true; };
    }, [meetingId]);

    const join = useCallback((c: PreJoinChoice) => {
        if (!meetingId) return;
        setIdentity(saveGuestIdentity(meetingId, c.displayName?.trim() || "Guest"));
        setChoice(c);
    }, [meetingId]);
    const leave = useCallback(() => setChoice(null), []);

    if (choice && meetingId && identity) {
        return (
            <MeetingRoom
                meetingId={meetingId}
                meetingSubject={info?.subject ?? "Meeting"}
                kind={info?.kind ?? "video"}
                scheduledEnd={info?.scheduledEnd ?? null}
                speakerId={choice.speakerId}
                guest={{
                    displayName: identity.name,
                    token: identity.token,
                    initialMicOn: choice.micOn,
                    initialCamOn: choice.camOn
                }}
                deviceIds={{ cameraId: choice.cameraId, micId: choice.micId }}
                onLeave={leave}
            />
        );
    }

    if (status === "loading") {
        return (
            <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-zinc-950 p-6 text-white">
                <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                <p className="text-sm text-white/60">Loading meeting…</p>
            </div>
        );
    }

    if (status === "notfound") {
        return (
            <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-zinc-950 p-6 text-white">
                <ShieldAlert className="h-10 w-10 text-amber-400" />
                <h1 className="text-lg font-semibold">Meeting unavailable</h1>
                <p className="max-w-sm text-center text-sm text-white/60">
                    This meeting link is invalid or the meeting has ended.
                </p>
            </div>
        );
    }

    if (info?.joinable === false) {
        return (
            <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-zinc-950 p-6 text-white">
                <ShieldAlert className="h-10 w-10 text-amber-400" />
                <h1 className="text-lg font-semibold">{info.subject}</h1>
                <p className="max-w-sm text-center text-sm text-amber-400">
                    This meeting isn't open for joining right now.
                </p>
                <p className="text-[11px] text-white/40">Powered by Meeting</p>
            </div>
        );
    }

    return (
        <PreJoin
            subject={info?.subject ?? "Meeting"}
            audioOnly={info?.kind === "audio"}
            askForName
            initialName={meetingId ? loadGuestIdentity(meetingId)?.name : undefined}
            joinLabel="Join meeting"
            onJoin={join}
        />
    );
}
